import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Crown, Medal, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Editable } from "./Editable";

const RANK_ICONS = {
    1: { Icon: Crown, color: "text-[#E53935]", bg: "bg-[#F2D8D5]" },
    2: { Icon: Trophy, color: "text-[#4A5568]", bg: "bg-[#F4F1EA]" },
    3: { Icon: Medal, color: "text-[#A0522D]", bg: "bg-[#F4F1EA]" },
};

const ClassLeaderboard = () => {
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/classes/leaderboard", { params: { limit: 8 } })
            .then((r) => setClasses(r.data || []))
            .catch(() => setClasses([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return null;

    const top = classes[0];
    const max = top?.total || 1;

    return (
        <section
            id="class-leaderboard"
            data-testid="class-leaderboard-section"
            className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-24"
        >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
                <div className="lg:col-span-1">
                    <Editable contentKey="class.tag" as="p" className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">
                        Class credit tally
                    </Editable>
                    <Editable contentKey="class.headline" as="h2" className="font-heading text-3xl sm:text-4xl text-[#1C252E] font-medium tracking-tight leading-tight mb-5">
                        Credit your classroom
                    </Editable>
                    <Editable contentKey="class.body" as="p" multiline className="text-[#4A5568] leading-relaxed mb-6">
                        At checkout, supporters may credit a purchase or donation to a classroom. Class credits help the foundation track community support and may be used to prioritize classroom activities, field trips, supplies, and meal support.
                    </Editable>
                    <Link
                        to="/donate"
                        data-testid="class-lb-donate-link"
                        className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-6 py-3 text-sm font-medium transition-all hover:-translate-y-0.5"
                    >
                        <Editable contentKey="class.cta" as="span">Credit a classroom</Editable>
                        <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </Link>
                </div>

                <div className="lg:col-span-2" data-testid="class-leaderboard-list">
                    {classes.length === 0 ? (
                        <div
                            data-testid="class-leaderboard-empty"
                            className="bg-white border border-black/5 rounded-2xl p-10 text-center"
                        >
                            <Trophy className="w-10 h-10 text-[#E53935] mx-auto mb-3" strokeWidth={1.5} />
                            <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-2">
                                Be the first to put a class on the board
                            </h3>
                            <p className="text-sm text-[#4A5568] max-w-sm mx-auto">
                                Add your child's teacher when you donate and start the race.
                            </p>
                        </div>
                    ) : (
                        <ul className="space-y-3">
                            {classes.map((c) => {
                                const pct = (c.total / max) * 100;
                                const rank = RANK_ICONS[c.rank];
                                return (
                                    <li
                                        key={c.class_tag}
                                        data-testid={`class-row-${c.rank}`}
                                        className={`relative overflow-hidden bg-white border rounded-2xl p-5 transition-all ${
                                            c.rank === 1
                                                ? "border-[#E53935]/40 shadow-[0_8px_30px_rgba(229,57,53,0.08)]"
                                                : "border-black/5"
                                        }`}
                                    >
                                        <div
                                            className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#F2D8D5]/40 to-transparent"
                                            style={{ width: `${pct}%` }}
                                            aria-hidden="true"
                                        />
                                        <div className="relative flex items-center gap-4">
                                            <div
                                                className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center font-heading text-lg font-medium ${
                                                    rank ? rank.bg : "bg-[#F4F1EA]"
                                                }`}
                                            >
                                                {rank ? (
                                                    <rank.Icon className={`w-5 h-5 ${rank.color}`} strokeWidth={1.5} />
                                                ) : (
                                                    <span className="text-[#1C252E]">{c.rank}</span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p
                                                    className="font-heading text-lg text-[#1C252E] font-medium truncate"
                                                    data-testid={`class-name-${c.rank}`}
                                                >
                                                    {c.class_tag}
                                                </p>
                                                <p className="text-xs text-[#4A5568]">
                                                    {c.donor_count} family member{c.donor_count === 1 ? "" : "s"} · {c.gift_count} gift{c.gift_count === 1 ? "" : "s"}
                                                </p>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <p
                                                    className="font-heading text-xl text-[#1C252E] font-medium"
                                                    data-testid={`class-total-${c.rank}`}
                                                >
                                                    ${Math.round(c.total).toLocaleString()}
                                                </p>
                                                <p className="text-xs text-[#4A5568] uppercase tracking-wider">raised</p>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ClassLeaderboard;
